'use client'

import type { UseFormReturn } from 'react-hook-form'
import {
  Button,
  CloseButton,
  Dialog,
  Input,
  Portal,
  Spinner,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { AppField } from '@/shared'
import type { ExaminationFormValues } from '@/entities/visit/model/examination.schema'
import { useMedicalDeferralForm } from '../model/use-medical-deferral-form'

interface Props {
  open: boolean
  visitId: string
  donorId: string
  examinationForm: UseFormReturn<ExaminationFormValues>
  onClose: () => void
}

export function MedicalDeferralModal({
  open,
  visitId,
  donorId,
  examinationForm,
  onClose,
}: Props) {
  const { t } = useTranslation()

  const { form, isPending, onSubmit } = useMedicalDeferralForm({
    visitId,
    donorId,
    examinationForm,
    onSuccess: onClose,
  })

  const {
    register,
    reset,
    formState: { errors },
  } = form

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose()
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" maxW="520px">
            <Dialog.Header pb={4}>
              <Dialog.Title fontSize="lg" fontWeight="bold">
                {t('donor_detail.medical_history.deferral_modal.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger
                asChild
                position="absolute"
                top={4}
                right={4}
              >
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={2}>
              <form id="medical-deferral-form" onSubmit={onSubmit}>
                <AppField.Root invalid={!!errors.deferral_end_date}>
                  <AppField.Label fontSize="sm">
                    {t(
                      'donor_detail.medical_history.deferral_modal.date_label',
                    )}
                  </AppField.Label>
                  <Input
                    {...register('deferral_end_date')}
                    type="date"
                    size="sm"
                    bg="bg.subtle"
                    borderColor={
                      errors.deferral_end_date ? undefined : 'border'
                    }
                    borderRadius="md"
                  />
                  <AppField.ErrorText>
                    {errors.deferral_end_date?.message}
                  </AppField.ErrorText>
                </AppField.Root>
              </form>
            </Dialog.Body>

            <Dialog.Footer pt={4} justifyContent="flex-end">
              <Button
                type="submit"
                form="medical-deferral-form"
                bg="primary.solid"
                color="white"
                borderRadius="md"
                size="sm"
                px={6}
                _hover={{ opacity: 0.85 }}
                _active={{ opacity: 0.7 }}
                disabled={isPending || !donorId}
              >
                {isPending ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  t('donor_detail.medical_history.save')
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
